import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Col, InputGroup, Modal, Row, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiCardRemoveOutline, mdiChevronLeft, mdiClipboardPulseOutline, mdiPulse } from "@mdi/js";
import Select from "react-select";

import { useGetGruposGrupo, useGetGruposListar, useGetMonitoresMonitor, useGetMonitoresTasksTask } from "../../config/swagger";
import { SelectGroupStyle } from "../../config/select";
import { ExibirMonitorResponse } from "../../model";
import { getSaudeDescricao, getSaudeIcon, ordenarPorSaude } from "../../utils/saude";
import { formatarTempoChecagem } from "../../utils/tempo";
import { SkeletonCount } from "../../config/defines";

import DashboardCard from "../../components/DashboardCard";
import TarefasCard from "../../components/TarefasCard";
import TarefaCard from "../../components/TarefaCard";
import LayoutSingle from "../../components/LayoutSingle";

export default function StatusGrupo() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const token = searchParams.get("t");
	const { id } = useParams();

	const [enable, setEnable] = useState(false);
	const [filtroGrupo, setFiltroGrupo] = useState<{ label: string; value: number | undefined } | undefined>();
	const [filtroGrupoOptions, setFiltroGrupoOptions] = useState<{ label: string; value: number }[]>([]);

	const [tarefa, setTarefa] = useState<NonNullable<ExibirMonitorResponse["tasks"]>[number] | null>(null);

	const [monitorId, setMonitorId] = useState<number | undefined>();
	const [tarefasModal, setTarefasModal] = useState(false);

	const queryGrupos = useGetGruposListar({ offset: 0 }, { query: { enabled: enable } });
	const queryGrupo = useGetGruposGrupo({ id: Number(id) }, { query: { enabled: enable && !!id, refetchInterval: 60 * 1000 } });
	const queryMonitor = useGetMonitoresMonitor({ id: monitorId }, { query: { enabled: enable && monitorId !== undefined } });
	const queryTarefa = useGetMonitoresTasksTask({ id: tarefa?.id }, { query: { enabled: enable && !!id && !!tarefa } });

	useEffect(() => {
		if (token) {
			sessionStorage.setItem("user", JSON.stringify({ token: token }));
			setEnable(true);
		}
	}, [token]);

	useEffect(() => {
		if (queryGrupos.data?.Result === 1 && queryGrupos.data?.Data) {
			setFiltroGrupoOptions(
				queryGrupos.data.Data.map((item) => {
					return { label: `${item.nome}`, value: item.id! };
				})
			);
			if (id) {
				const grupoSelecionado = queryGrupos.data.Data.find((item) => item.id === Number(id));
				if (grupoSelecionado) {
					setFiltroGrupo({ label: grupoSelecionado.nome || "", value: grupoSelecionado.id });
				}
			}
		}
	}, [queryGrupos.data, id]);

	function handleVoltar() {
		navigate(`/status?t=${token}`);
	}

	return (
		<LayoutSingle header={"Status"}>
			<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
				<div className="d-flex align-items-center" onClick={handleVoltar} style={{ cursor: "pointer" }}>
					<Icon path={mdiChevronLeft} size={1} className="me-1" />
					<Icon path={mdiClipboardPulseOutline} size={1} className="me-2" />
					Dashboard Grupo
				</div>
				{(queryGrupo.isFetching || queryGrupos.isFetching) && <Spinner size="sm" className="ms-2" variant="secondary" />}
				<div className="float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
					<InputGroup className="fs-6" style={{ width: 300 }}>
						<InputGroup.Text className="bg-white px-2">
							<span className="small">Grupo</span>
						</InputGroup.Text>
						<Select
							placeholder={"Selecione"}
							className="small bg-white rounded"
							value={filtroGrupo}
							noOptionsMessage={() => {
								return "Nenhuma opção";
							}}
							options={filtroGrupoOptions}
							isLoading={queryGrupos.isLoading}
							onChange={(value) => {
								setFiltroGrupo(value ?? undefined);
								navigate(`/statusGrupo/${token}/${value?.value}`);
							}}
							styles={SelectGroupStyle}
						/>
					</InputGroup>
				</div>
			</h5>

			<Row className="mb-4">
				{queryGrupo.isLoading && (
					<>
						<Col md={4}>
							<DashboardCard title="&nbsp;" footer="&nbsp;" icon={mdiPulse} loading={true} />
						</Col>
						<Col md={4}>
							<DashboardCard title="&nbsp;" footer="&nbsp;" icon={mdiPulse} loading={true} />
						</Col>
						<Col md={4}>
							<DashboardCard title="&nbsp;" footer="&nbsp;" icon={mdiPulse} loading={true} />
						</Col>
					</>
				)}

				{!queryGrupo.isLoading && (
					<>
						{queryGrupo.data?.Result !== 1 && (
							<Col md={12}>
								<Alert variant="secondary" className="text-center">
									{queryGrupo.data?.Message || "Problema na operação. Tente novamente mais tarde"}
								</Alert>
							</Col>
						)}

						{queryGrupo.data?.Result === 1 && (
							<>
								{!queryGrupo.data?.Data?.monitores || queryGrupo.data.Data.monitores.length === 0 ? (
									<Alert variant="light" className="text-center p-4 d-flex justify-content-center align-items-center">
										<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
										Nenhum registro encontrado
									</Alert>
								) : (
									ordenarPorSaude(queryGrupo.data.Data.monitores).map((item, index) => {
										const iconClass = getSaudeIcon(item.saude);
										const saudeDescricao = getSaudeDescricao(item.saude);
										return (
											<Col md={4} className="mb-3" key={index}>
												<DashboardCard
													icon={mdiPulse}
													iconClass={iconClass}
													title={item.nome || "Sem nome"}
													value={saudeDescricao}
													footer={formatarTempoChecagem(item.dataUltimaChecagem || "", item.ultimaLatencia || "")}
													onClick={() => {
														setMonitorId(item.id);
														setTarefasModal(true);
													}}
												/>
											</Col>
										);
									})
								)}
							</>
						)}
					</>
				)}
			</Row>

			<Modal
				show={tarefasModal}
				onHide={() => {
					setTarefasModal(false);
					setTarefa(null);
				}}
				size="xl"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>
						{tarefa ? "Tarefa" : "Tarefas"}
						<div className="fs-6 fw-normal">
							<small>{queryMonitor.data?.Data?.nome}</small>
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{tarefa && (
						<>
							{queryTarefa.isLoading && <TarefaCard skeleton />}
							{queryTarefa.data?.Data && <TarefaCard data={queryTarefa.data.Data} />}
						</>
					)}
					{!tarefa && (
						<>
							{queryMonitor.isLoading &&
								Array.from({ length: Number(SkeletonCount) }, (_, index) => {
									return <TarefasCard skeleton />;
								})}
							{!queryMonitor.isLoading && (
								<>
									{queryMonitor.data?.Data?.tasks && queryMonitor.data?.Data?.tasks.length > 0 ? (
										queryMonitor.data.Data.tasks.map((item: any, index: number) => (
											<TarefasCard
												key={index}
												data={item}
												onClick={() => {
													setTarefa(item);
												}}
											/>
										))
									) : (
										<Alert variant="light" className="text-center">
											Nenhuma tarefa para este monitor.
										</Alert>
									)}
								</>
							)}
						</>
					)}
				</Modal.Body>
				{tarefa && (
					<Modal.Footer>
						<Button
							variant="secondary"
							onClick={() => {
								setTarefa(null);
							}}
						>
							Voltar
						</Button>
					</Modal.Footer>
				)}
			</Modal>
		</LayoutSingle>
	);
}
